/* HR — Departments page
 *  endpoints used:
 *    GET    /api/departments?active_only=bool
 *    POST   /api/departments
 *    PATCH  /api/departments/:id
 *    DELETE /api/departments/:id
 *    GET    /api/employees/search?q=&limit=  (manager picker)
 */

/* Expressive UI only (no-op under Bento; removed with the other gx-css-* on router fallback).
 * EmployeePicker is an outlined field whose wrapper carries the hover / focus ring (same values as
 * the shared input rules in gx-css-pages), so the inner <input> opts out of the shared
 * input-focus rule (the :not(#…) adds ID specificity on purpose). */
if (window.GxCore) window.GxCore.injectCss('gx-css-p-departments', String.raw`
html[data-ui-shell="expressive"] .gx-emppick:hover:not(:focus-within){border-color:var(--gx-on-v)!important}
html[data-ui-shell="expressive"] .gx-emppick:focus-within{background-color:var(--gx-card)!important;border-color:var(--gx-p)!important;outline:2px solid var(--gx-p);outline-offset:-2px}
html[data-ui-shell="expressive"] .gx-emppick input.gx-emppick-q:not(#gx-none):focus{outline:none!important;background-color:transparent!important;border-color:transparent!important;box-shadow:none!important}
`);

function EmployeePicker({ value, valueLabel, onChange, placeholder = '搜尋員工…' }) {
  const [q, setQ] = React.useState('');
  const [hits, setHits] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  React.useEffect(() => {
    if (!open || !q || q.length < 1) { setHits([]); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      api('GET', `/employees/search?q=${encodeURIComponent(q)}&limit=10`)
        .then(rows => { if (alive) setHits(rows || []); })
        .catch(() => { if (alive) setHits([]); })
        .finally(() => { if (alive) setLoading(false); });
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  const display = value
    ? (valueLabel || `#${value}`)
    : '';

  const gc = GxT.color;
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Expressive: the wrapper is the outlined field (GX inputStyle recipe); focus ring is on the
          wrapper via :focus-within (gx-css-p-departments) so the inner input stays transparent. */}
      <div className={GxCore.cls(undefined, 'gx-emppick')} style={uiStyle({
        display: 'flex', alignItems: 'center', gap: 6,
        border: `1px solid ${HR.rule}`, borderRadius: 4, background: HR.surface,
        padding: '4px 8px 4px 11px',
      }, {
        minHeight: 40, padding: '4px 8px 4px 12px',
        border: `1px solid ${gc.ol}`, borderRadius: GxT.radius.input,
        background: gc.card,
      })}>
        {value && (
          <span style={uiStyle({
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: HR.accentSoft, color: HR.accent,
            padding: '3px 8px', borderRadius: 3, fontSize: FontSizes.xxs, fontWeight: 500,
          }, {
            background: gc.pc, color: gc.onPc, padding: '3px 4px 3px 10px',
            borderRadius: GxT.radius.chip, fontSize: 13, fontWeight: 700,
          })}>
            {display}
            <button
              type="button"
              onClick={() => onChange(null, null)}
              style={uiStyle({ background: 'transparent', border: 'none', cursor: 'pointer', color: HR.accent, padding: 0, lineHeight: 1, fontSize: FontSizes.sm }, {
                color: gc.onPc, minWidth: 24, minHeight: 24, borderRadius: GxT.radius.chipSm, fontSize: 16,
              })}
              aria-label="移除"
            >×</button>
          </span>
        )}
        <input
          type="text"
          className={GxCore.cls(undefined, 'gx-emppick-q')}
          value={q}
          placeholder={value ? '' : placeholder}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          style={uiStyle({
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontSize: FontSizes.xs, fontFamily: HR.sans, color: HR.ink, padding: '4px 0',
            minWidth: 100,
          }, { fontSize: 14, color: gc.on })}
        />
      </div>
      {open && (q.length > 0) && (
        <div style={uiStyle({
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50,
          background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 5,
          boxShadow: '0 8px 24px rgba(0,0,0,0.10)', maxHeight: 240, overflowY: 'auto',
        }, { border: 'none', borderRadius: GxT.radius.inner, boxShadow: GxT.shadow.pop, padding: 6 })}>
          {loading ? (
            <div style={{ padding: '12px 14px', fontSize: FontSizes.xxs, color: HR.inkFaint }}>搜尋中…</div>
          ) : hits.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: FontSizes.xxs, color: HR.inkFaint }}>無符合員工</div>
          ) : hits.map(h => (
            <div
              key={h.id}
              onClick={() => {
                const label = `${h.name}${h.emp_code ? ` (${h.emp_code})` : ''}`;
                onChange(h.id, label);
                setQ(''); setOpen(false);
              }}
              style={uiStyle({
                padding: '8px 12px', fontSize: FontSizes.xs, cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10,
              }, { padding: '10px 12px', fontSize: 14, borderRadius: GxT.radius.chip })}
              onMouseEnter={(e) => e.currentTarget.style.background = HR.surfaceAlt}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <span>
                <span style={{ fontWeight: uiPick(500, 700) }}>{h.name}</span>
                {h.name_en && <span style={{ color: HR.inkFaint, marginLeft: 6, fontStyle: uiPick('italic', 'normal') }}>{h.name_en}</span>}
              </span>
              <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint, fontFamily: HR.mono }}>
                {h.emp_code || '—'}{h.department_name ? ` · ${h.department_name}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DeptForm({ initial, onCancel, onSaved }) {
  const toast = useToast();
  const isEdit = !!(initial && initial.id);
  const [code, setCode] = React.useState(initial?.code || '');
  const [name, setName] = React.useState(initial?.name || '');
  const [active, setActive] = React.useState(initial ? !!initial.active : true);
  const [managerId, setManagerId] = React.useState(initial?.manager_id || null);
  const [managerLabel, setManagerLabel] = React.useState(initial?.manager_name || '');
  const [busy, setBusy] = React.useState(false);
  const [errors, setErrors] = React.useState({});

  const submit = async () => {
    if (!name.trim()) { setErrors({ name: '部門名稱必填' }); return; }
    setBusy(true); setErrors({});
    const payload = {
      code: code.trim() || null,
      name: name.trim(),
      manager_id: managerId,
      active,
    };
    try {
      if (isEdit) {
        await api('PATCH', `/departments/${initial.id}`, payload);
        toast('部門已更新', 'ok');
      } else {
        await api('POST', '/departments', payload);
        toast('部門已建立', 'ok');
      }
      onSaved();
    } catch (e) {
      toast(e.detail || '儲存失敗', 'error');
    } finally { setBusy(false); }
  };

  return (
    <Modal
      open
      onClose={busy ? null : onCancel}
      title={isEdit ? `編輯部門 — ${initial.name}` : '新增部門'}
      subtitle={isEdit ? 'Edit Department' : 'New Department'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={busy}>
            {busy ? '儲存中…' : (isEdit ? '儲存變更' : '建立部門')}
          </Button>
        </>
      }
    >
      <div className="hr-dept-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Field label="部門代碼" hint="UNIQUE，可留空">
          <TextInput value={code} onChange={setCode} placeholder="e.g. ENG" mono />
        </Field>
        <Field label="部門名稱" required error={errors.name}>
          <TextInput value={name} onChange={setName} placeholder="e.g. 工程部" autoFocus />
        </Field>
        <Field label="部門主管" hint="選填，從在職員工中選" span={2}>
          <EmployeePicker value={managerId} valueLabel={managerLabel} onChange={(id, label) => { setManagerId(id); setManagerLabel(label || ''); }} />
        </Field>
        <Field label="狀態" span={2}>
          <Checkbox checked={active} onChange={setActive} label="啟用此部門（active）" />
        </Field>
      </div>
    </Modal>
  );
}

function DepartmentsPage() {
  const toast = useToast();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activeOnly, setActiveOnly] = React.useState(true);
  const [editTarget, setEditTarget] = React.useState(null);  // null | 'new' | dept obj
  const [delTarget, setDelTarget] = React.useState(null);
  const [delBusy, setDelBusy] = React.useState(false);

  const refresh = React.useCallback(() => {
    setLoading(true);
    api('GET', `/departments?active_only=${activeOnly}`)
      .then(rows => setItems(rows || []))
      .catch(e => toast(e.detail || '載入失敗', 'error'))
      .finally(() => setLoading(false));
  }, [activeOnly, toast]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const doDelete = async () => {
    if (!delTarget) return;
    setDelBusy(true);
    try {
      await api('DELETE', `/departments/${delTarget.id}`);
      toast('部門已刪除', 'ok');
      setDelTarget(null);
      refresh();
    } catch (e) {
      toast(e.detail || '刪除失敗', 'error');
    } finally { setDelBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="人員管理 · Organization"
        title="部門"
        italic="Departments"
        action={
          <Button variant="primary" size="lg" icon="plus" onClick={() => setEditTarget('new')}>
            新增部門
          </Button>
        }
      />

      <Card
        className="hr-dept-card"
        eyebrow="Directory"
        title="部門清單"
        italic={`${items.length} 個部門`}
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Checkbox
              checked={activeOnly}
              onChange={setActiveOnly}
              label="僅顯示啟用中"
            />
            <Button variant="ghost" size="sm" icon="refresh" onClick={refresh}>重新整理</Button>
          </div>
        }
        padding="0"
      >
        {loading ? (
          <SkeletonTable rows={6} />
        ) : items.length === 0 ? (
          <EmptyState
            icon="folder"
            title="尚無部門資料"
            hint={activeOnly ? '可能全部停用中，試試取消「僅顯示啟用中」。' : '建立第一個部門開始建構組織架構。'}
            action={<Button variant="primary" icon="plus" onClick={() => setEditTarget('new')}>新增部門</Button>}
          />
        ) : (
          <div className="hr-dept-table">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={uiStyle({ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }, { letterSpacing: 0, textTransform: 'none', color: GxT.color.onV, fontSize: 12 })}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500 }}>代碼</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>名稱</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>主管</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>狀態</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>更新時間</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500, width: 160 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map(d => (
                <tr
                  key={d.id}
                  className="hr-row"
                  style={{ borderTop: `1px solid ${HR.ruleSoft}` }}
                >
                  <td style={{ padding: '14px 16px 14px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                    {d.code || '—'}
                  </td>
                  <td style={{ padding: '14px 16px', fontWeight: uiPick(500, 700) }}>
                    {d.name}
                  </td>
                  <td style={{ padding: '14px 16px', color: HR.inkSoft }}>
                    {d.manager_name ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span style={uiStyle({
                          width: 22, height: 22, borderRadius: '50%', background: '#D6CEB9', color: HR.ink,
                          display: 'grid', placeItems: 'center', fontSize: FontSizes.trading_xs, fontWeight: 600,
                        }, { width: 26, height: 26, background: GxT.color.tc, color: GxT.color.onTc, fontSize: 12, fontWeight: 800 })}>{d.manager_name.slice(0, 1)}</span>
                        {d.manager_name}
                      </span>
                    ) : <span style={{ color: HR.inkFaint, fontStyle: uiPick('italic', 'normal') }}>未指派</span>}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    {d.active
                      ? <Pill tone="accent" dot>啟用中</Pill>
                      : <Pill tone="neutral" dot>已停用</Pill>}
                  </td>
                  <td style={{ padding: '14px 16px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>
                    {fmtDateTime(d.updated_at)}
                  </td>
                  <td style={{ padding: '14px 24px 14px 16px', textAlign: 'right' }}>
                    <span className="hr-row-actions" style={{ display: 'inline-flex', gap: 6 }}>
                      <Button size="sm" variant="ghost" onClick={() => setEditTarget(d)}>編輯</Button>
                      <Button size="sm" variant="ghost" onClick={() => setDelTarget(d)} style={{ color: HR.down }}>刪除</Button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        <style>{`
          .hr-row:hover { background: ${HR.surfaceAlt}; }
          /* Actions always visible — no hover-reveal (discoverability fix) */
        `}</style>
      </Card>

      {editTarget && (
        <DeptForm
          initial={editTarget === 'new' ? null : editTarget}
          onCancel={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); refresh(); }}
        />
      )}

      <ConfirmDialog
        open={!!delTarget}
        title={delTarget ? `刪除部門「${delTarget.name}」？` : ''}
        message="此操作無法復原。若部門尚有在職員工歸屬，系統會拒絕刪除（請先轉調員工）。"
        danger
        busy={delBusy}
        onCancel={() => setDelTarget(null)}
        onConfirm={doDelete}
      />
    </>
  );
}

window.DepartmentsPage = DepartmentsPage;
window.EmployeePicker = EmployeePicker;
